<template>
  <div>
    <v-office-filter
      :objectsFetched="objectsFetched"
      :objects="mapObjects"
      :multiSelectFilters="multiSelectFilters"
      :numberOfItems="totalCount"
      @setMultiSelectFilter="handleSelectedFilters"
      @setSelectedPlacesLength="handleSelectedPlaces"
      @setSelectedTypesOfBuildingsLength="handleSelectedTypesOfBuildings"
      @setMinSize="handleMinSize"
      @setMaxSize="handleMaxSize"
      @setMinWorkplaces="handleMinWorkplaces"
      @setMaxWorkplaces="handleMaxWorkplaces"
      @setSortingOption="handleSortingOption"
      @setFilterIds="handleFilterIds"
      :apiKey="this.apiKey"
      :translationString="this.translationString"
      ref="officefilter"
    ></v-office-filter>
    <div class="c-object-listing o-width-limiter--full" style="display: block">
      <div
        v-if="
          isDesktop &&
          (processedFilters.length > 0 || sizeBadgeText || workplacesBadgeText)
        "
        class="selected-filters"
      >
        <span
          v-for="(filter, index) in processedFilters"
          :key="index"
          class="badge"
        >
          {{ filter }}
          <button @click="removeBadge(index)">
            <span aria-hidden="true" class="c-icon c-icon--xss">
              <svg>
                <use xlink:href="#icon-close"></use>
              </svg>
            </span>
          </button>
        </span>
        <span v-if="sizeBadgeText" class="badge">
          {{ sizeBadgeText }}
          <button @click="removeSizeBadge">
            <span aria-hidden="true" class="c-icon c-icon--xss">
              <svg>
                <use xlink:href="#icon-close"></use>
              </svg>
            </span>
          </button>
        </span>
        <span v-if="workplacesBadgeText" class="badge">
          {{ workplacesBadgeText }}
          <button @click="removeWorkplacesBadge">
            <span aria-hidden="true" class="c-icon c-icon--xss">
              <svg>
                <use xlink:href="#icon-close"></use>
              </svg>
            </span>
          </button>
        </span>
      </div>
      <div
        v-if="$refs.officefilter && $refs.officefilter.showMap"
        class="c-office-filter__sorting-container"
        :class="{ 'c-office-filter__sorting-container-mobile': !isDesktop }"
      >
        <div class="c-office-filter__container">
          <div class="c-office-filter__right-item">
            <span>{{ this.translate.sorton }}:</span>
            <button
              class="c-office-filter__sorting-select"
              id="sorting-select"
              @click="showSortingOptions = !showSortingOptions"
            >
              {{ activeSorting.display }}
              <div
                aria-hidden="true"
                class="c-icon c-icon--xs c-office-filter-dropdown__toggle-icon"
              >
                <svg>
                  <use :xlink:href="`#icon-select-arrow`"></use>
                </svg>
              </div>
            </button>
            <ul
              v-if="showSortingOptions"
              v-click-outside="handleClickOutside"
              class="c-office-filter__sorting-options-dropdown"
            >
              <li
                class="c-office-filter__sorting-option"
                :class="{
                  'c-office-filter__sorting-option--active':
                    option === activeSorting,
                }"
                v-for="(option, index) in sortOptions"
                :key="index"
                @click="selectSortingOption(option)"
              >
                <div
                  v-if="option === activeSorting"
                  aria-hidden="true"
                  class="c-icon c-icon--xs c-office-filter__check-icon"
                >
                  <svg>
                    <use :xlink:href="`#icon-check`"></use>
                  </svg>
                </div>
                {{ option.display }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="c-object-listing__items">
        <div
          class="c-object-listing__item"
          v-for="(object, index) in filteredObjects"
          :key="index"
        >
          <v-object-card
            :url="normalizeProperty(object, 'url')"
            :image="normalizeProperty(object, 'image')"
            :images="normalizeProperty(object, 'images')"
            :title="normalizeProperty(object, 'title')"
            :sizeFrom="normalizeProperty(object, 'sizeFrom')"
            :sizeTo="normalizeProperty(object, 'sizeTo')"
            :city="normalizeProperty(object, 'city')"
            :district="normalizeProperty(object, 'district')"
            :id="objectId(object)"
            :address="normalizeProperty(object, 'address')"
            :premiseTypes="
              normalizeProperty(object, 'premiseTypes') ||
              normalizeProperty(object, 'PremiseTypes')
            "
            :features="normalizeProperty(object, 'selectedFeatures')"
            :selectedFeatures="normalizeProperty(object, 'selectedFeatures')"
            :overflowFeaturesCount="
              normalizeProperty(object, 'overflowFeaturesCount')
            "
            :numberOfWorkplaces="
              normalizeProperty(object, 'numberOfWorkplaces')
            "
            :minNumberOfWorkplaces="
              normalizeProperty(object, 'minNumberOfWorkplaces')
            "
            :maxNumberOfWorkplaces="
              normalizeProperty(object, 'maxNumberOfWorkplaces')
            "
            :pricePerSquare="normalizeProperty(object, 'pricePerSquare')"
          >
          </v-object-card>
        </div>
      </div>
      <div class="c-object-listing__loading" v-if="isLoading">
        <div class="c-loading">
          <span class="t-visually-hidden">{{ this.translate.loading }}...</span>
        </div>
      </div>
      <!-- Pagination: "Visa fler lokaler" button -->
      <div
        v-if="!isLoading && hasMorePages"
        class="c-object-listing__pagination"
      >
        <p class="c-object-listing__pagination-count">
          {{ translate.showing || "Visar" }}
          <span class="c-object-listing__pagination-count-highlight">
            {{ filteredObjects.length }} {{ translate.of || "av" }}
            {{ totalCount }}
          </span>
          {{ translate.premises || "lokaler" }}
        </p>
        <button
          class="c-object-listing__pagination-button"
          :disabled="isLoadingMore"
          @click="loadMore"
        >
          <span
            v-if="isLoadingMore"
            class="c-object-listing__pagination-button-loading"
          >
            {{ translate.loading || "Laddar" }}...
          </span>
          <span v-else>
            {{ translate.showmorepremises || "Visa fler lokaler" }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import VOfficeFilter from "@/components/filter/VOfficeFilter.vue";
import VObject from "@/components/object/VObject.vue";
import VObjectCard from "@/components/object-card/VObjectCard.vue";
import vClickOutside from "v-click-outside";

export default {
  name: "VObjectListing",
  directives: {
    clickOutside: vClickOutside.directive,
  },
  components: {
    VOfficeFilter,
    VObject,
    VObjectCard,
  },
  props: {
    apiUrl: {
      type: String,
      default: "",
    },
    resetAllText: {
      type: String,
      default: "Återställ val",
    },
    loadingText: {
      type: String,
      default: "Laddar objekt",
    },
    apiKey: {
      type: String,
      required: false,
      default: "",
    },
    translationString: {
      type: [Object, String],
      default: () => ({}),
    },
    useMockData: {
      type: Boolean,
      default: false,
    },
    mockData: {
      type: [Object, String],
      default: null,
    },
    districtId: {
      type: [String, Number],
      default: "",
    },
    cityId: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      activeSorting: {},
      activeSortingId: null,
      objectsFetched: false,
      isLoading: true,
      isLoadingMore: false,
      filterData: null,
      showSortingOptions: false,
      sortOptions: [],
      selectedFilters: [],
      processedFilters: [],
      selectedPlaces: 0,
      selectedTypesOfBuildings: 0,
      selectedCityIds: [],
      selectedDistrictIds: [],
      selectedPremiseTypes: [],
      minSize: "",
      maxSize: "",
      minNumberOfWorkplaces: "",
      maxNumberOfWorkplaces: "",
      isDesktop: true,
      filteredObjects: [],
      translate: {},
      // Debounce timer for filter changes
      filterDebounceTimer: null,
    };
  },

  methods: {
    /**
     * Normalize property access to handle both camelCase and PascalCase
     * Tries camelCase first, then PascalCase
     */
    normalizeProperty(object, camelCaseKey) {
      if (!object) return null;

      // Try camelCase first (e.g., 'url')
      if (object[camelCaseKey] !== undefined) {
        return object[camelCaseKey];
      }

      // Try PascalCase (e.g., 'Url')
      const pascalCaseKey =
        camelCaseKey.charAt(0).toUpperCase() + camelCaseKey.slice(1);
      if (object[pascalCaseKey] !== undefined) {
        return object[pascalCaseKey];
      }

      return null;
    },

    /**
     * Generate a safe ID string for an object
     */
    objectId(object) {
      const district = (object.district || "")
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "");
      const city = (object.city || "").toLowerCase().replace(/[^a-z0-9-]/g, "");
      return district + "-" + city;
    },

    checkScreenSize() {
      this.isDesktop = window.innerWidth >= 1200;
    },

    addBadge(filter) {
      if (filter && !this.selectedFilters.includes(filter)) {
        this.selectedFilters.push(filter);
      }
    },

    removeBadge(index) {
      const removedFilter = this.selectedFilters[index];
      this.selectedFilters.splice(index, 1);

      this.$refs.officefilter.handleRemovePlace(removedFilter);
      this.handleSelectedFilters(this.selectedFilters);
    },

    /**
     * Build the POST payload from current filter/sort state
     */
    buildPayload(page) {
      const payload = {
        page: page || 1,
      };

      if (this.activeSortingId) {
        payload.sort = this.activeSortingId;
      }

      // city filters
      if (this.selectedCityIds && this.selectedCityIds.length > 0) {
        payload.cityIds = this.selectedCityIds;
      }

      //district filters
      if (this.selectedDistrictIds && this.selectedDistrictIds.length > 0) {
        payload.districtIds = this.selectedDistrictIds;
      }

      // Premise type filters
      if (this.selectedPremiseTypes && this.selectedPremiseTypes.length > 0) {
        payload.types = this.selectedPremiseTypes;
      }

      if (this.minSize !== "" && this.minSize !== null) {
        payload.areaMin = parseInt(this.minSize);
      }

      if (this.maxSize !== "" && this.maxSize !== null) {
        payload.areaMax = parseInt(this.maxSize);
      }

      if (
        this.maxNumberOfWorkplaces !== "" &&
        this.maxNumberOfWorkplaces !== null
      ) {
        payload.maxNumberOfWorkplaces = parseInt(this.maxNumberOfWorkplaces);
      }

      if (
        this.minNumberOfWorkplaces !== "" &&
        this.minNumberOfWorkplaces !== null
      ) {
        payload.minNumberOfWorkplaces = parseInt(this.minNumberOfWorkplaces);
      }

      return payload;
    },

    // key we use to stash filters in sessionStorage, tied to the page path
    // so we don't mix up filters between different listing pages
    filterStorageKey() {
      return `objectListingFilters:${window.location.pathname}`;
    },

    // stash the current filters so if someone clicks into an object and hits
    // back, they land back on the same filtered view instead of a reset one
    saveFiltersToSession() {
      try {
        const state = {
          selectedCityIds: this.selectedCityIds,
          selectedDistrictIds: this.selectedDistrictIds,
          selectedPremiseTypes: this.selectedPremiseTypes,
          selectedFilters: this.selectedFilters,
          minSize: this.minSize,
          maxSize: this.maxSize,
          minNumberOfWorkplaces: this.minNumberOfWorkplaces,
          maxNumberOfWorkplaces: this.maxNumberOfWorkplaces,
          activeSortingId: this.activeSortingId,
        };
        sessionStorage.setItem(this.filterStorageKey(), JSON.stringify(state));
      } catch (e) {}
    },

    // restore filters from session storage, returns true if we actually found something
    restoreFiltersFromSession() {
      try {
        const raw = sessionStorage.getItem(this.filterStorageKey());
        if (!raw) return false;

        const state = JSON.parse(raw);

        this.selectedCityIds = state.selectedCityIds || [];
        this.selectedDistrictIds = state.selectedDistrictIds || [];
        this.selectedPremiseTypes = state.selectedPremiseTypes || [];
        this.selectedFilters = state.selectedFilters || [];
        this.minSize = state.minSize || "";
        this.maxSize = state.maxSize || "";
        this.minNumberOfWorkplaces = state.minNumberOfWorkplaces || "";
        this.maxNumberOfWorkplaces = state.maxNumberOfWorkplaces || "";
        if (state.activeSortingId) {
          this.activeSortingId = state.activeSortingId;
        }

        return true;
      } catch (e) {
        return false;
      }
    },

    /**
     * Dispatch the appropriate fetch action based on mock mode.
     */
    dispatchFetch(payload, append) {
      if (this.useMockData) {
        return this.$store.dispatch("objects/fetchObjectsMock", {
          payload: payload,
          append: append,
          mockData: this.mockData,
        });
      }
      return this.$store.dispatch("objects/fetchObjectsPost", {
        apiUrl: this.postUrl,
        payload: payload,
        append: append,
      });
    },

    dispatchMapFetch(payload) {
      if (this.useMockData) {
        return this.$store.dispatch("objects/fetchObjectsMock", {
          payload: Object.assign({}, payload, { limit: 999 }),
          mockData: this.mockData,
          mapOnly: true,
        });
      }
      return this.$store.dispatch("objects/fetchObjectsForMap", {
        apiUrl: this.postUrl,
        payload: payload,
      });
    },

    /**
     * Perform a fresh fetch (page 1) with current filters.
     * Called on filter changes, sorting changes, etc.
     */
    fetchFiltered() {
      this.isLoading = true;
      this.saveFiltersToSession();
      const payload = this.buildPayload(1);

      Promise.all([
        this.dispatchFetch(payload, false),
        this.dispatchMapFetch(payload),
      ]).then(() => {
        this.filteredObjects = this.currentObjects;

        this.isLoading = false;
        this.objectsFetched = true;

        // Update sorting options from server response if available
        this.updateSortingFromServer();
      });
    },

    /**
     * Debounced version of fetchFiltered to avoid too many requests on rapid changes
     */
    debouncedFetchFiltered() {
      if (this.filterDebounceTimer) {
        clearTimeout(this.filterDebounceTimer);
      }
      this.filterDebounceTimer = setTimeout(() => {
        this.fetchFiltered();
      }, 300);
    },

    /**
     * Load more results (pagination - next page, appended)
     */
    loadMore() {
      if (this.isLoadingMore || !this.hasMorePages) return;

      this.isLoadingMore = true;
      const nextPage = this.currentPage + 1;
      const payload = this.buildPayload(nextPage);

      this.dispatchFetch(payload, true).then(() => {
        this.filteredObjects = this.currentObjects;
        this.isLoadingMore = false;
      });
    },

    /**
     * Extract active filter IDs from selectedFilters array and multiSelectFilters
     * to build proper ID-based payloads for the POST request.
     * Handles both normalized (camelCase with value/id) and raw API (PascalCase with Name/Id) formats.
     */
    extractFilterIds() {
      this.selectedCityIds = [];
      this.selectedDistrictIds = [];
      this.selectedPremiseTypes = [];
      // somehow we lost 'this' context in the loop below, so we need to reassign it here - workaround for now
      const vm = this;

      if (!this.multiSelectFilters || this.multiSelectFilters.length === 0) {
        return;
      }

      // Process location filter
      const locationFilter = this.multiSelectFilters.find(
        (f) => f.name === "location" || f.Name === "location",
      );
      if (
        locationFilter &&
        locationFilter.items &&
        Array.isArray(locationFilter.items)
      ) {
        locationFilter.items.forEach((item) => {
          const itemValue = item.value || item.Name;
          const itemId = item.id || item.Id;

          // Check if this is a city (has groupLabel or is an object with Districts property)
          const isCity =
            item.groupLabel ||
            (item.Districts && Array.isArray(item.Districts));

          if (isCity) {
            // City level - check if selected
            const isSelected = vm.selectedFilters.includes(itemValue);
            if (isSelected && itemId) {
              vm.selectedCityIds.push(itemId);
            }
          } else {
            // District level - check if selected
            const isSelected = vm.selectedFilters.includes(itemValue);
            if (isSelected && itemId) {
              vm.selectedDistrictIds.push(itemId);
            }
          }
        });
      }

      // Process premise type filter
      const premiseFilter = this.multiSelectFilters.find(
        (f) => f.name === "premisetype" || f.Name === "premisetype",
      );
      if (
        premiseFilter &&
        premiseFilter.items &&
        Array.isArray(premiseFilter.items)
      ) {
        premiseFilter.items.forEach((item) => {
          const itemValue =
            item.value || item.Name || (typeof item === "string" ? item : null);
          const itemId = item.id || item.Id || null;
          const isSelected = this.selectedFilters.includes(itemValue);
          if (isSelected && itemId) {
            this.selectedPremiseTypes.push(itemId);
          }
        });
      }
    },

    handleSelectedFilters(data) {
      this.selectedFilters = data;
      this.extractFilterIds();
      this.debouncedFetchFiltered();
    },

    handleSelectedPlaces(length) {
      this.selectedPlaces = length;
    },

    handleSelectedTypesOfBuildings(length) {
      this.selectedTypesOfBuildings = length;
    },

    handleMinSize(size) {
      this.minSize = size;
      this.debouncedFetchFiltered();
    },

    handleMaxSize(size) {
      this.maxSize = size;
      this.debouncedFetchFiltered();
    },

    handleMinWorkplaces(size) {
      this.minNumberOfWorkplaces = size;
      this.debouncedFetchFiltered();
    },
    handleMaxWorkplaces(size) {
      this.maxNumberOfWorkplaces = size;
      this.debouncedFetchFiltered();
    },

    handleSortingOption(option) {
      this.activeSortingId = option.value || option.Id || option.id || null;
      this.fetchFiltered();
    },

    handleFilterIds(filterIds) {
      this.selectedCityIds = filterIds.placeIds || [];
      this.selectedDistrictIds = filterIds.districtIds || [];
      this.selectedPremiseTypes = filterIds.premiseTypeIds || [];
      this.debouncedFetchFiltered();
    },

    selectSortingOption(option) {
      this.activeSorting = option;
      this.showSortingOptions = false;
      this.handleSortingOption(option);
    },

    /**
     * Update local sort options from the server response sorting data
     */
    updateSortingFromServer() {
      const serverSorting = this.$store.getters["objects/sorting"];
      if (serverSorting && serverSorting.Options) {
        this.sortOptions = serverSorting.Options.map((opt) => ({
          display: opt.Name || opt.name,
          value: opt.Id || opt.id,
          active: opt.Active || opt.active || false,
        }));

        // Set active sorting from server
        const activeOpt = this.sortOptions.find((o) => o.active);
        if (activeOpt) {
          this.activeSorting = activeOpt;
          this.activeSortingId = activeOpt.value;
        }
      }
    },

    removeSizeBadge() {
      this.$refs.officefilter.minSize = "";
      this.$refs.officefilter.maxSize = "";
    },

    removeWorkplacesBadge() {
      this.$refs.officefilter.minNumberOfWorkplaces = "";
      this.$refs.officefilter.maxNumberOfWorkplaces = "";
    },

    handleClickOutside() {
      if (this.activeFilter === "plats" && this.isDesktop) {
        this.activeFilter = "";
      } else if (this.activeFilter === "lokal" && this.isDesktop) {
        this.activeFilter = "";
      } else if (this.activeFilter === "yta" && this.isDesktop) {
        this.activeFilter = "";
      } else if (this.activeFilter === "arbetsplatser" && this.isDesktop) {
        this.activeFilter = "";
      }

      if (this.showSortingOptions && this.isDesktop) {
        this.showSortingOptions = false;
      }
    },
  },

  computed: {
    ...mapGetters("objects", {
      currentObjects: "currentObjects",
      mapObjects: "mapObjects",
      multiSelectFilters: "multiSelectFilters",
      loading: "loading",
      filtersApplied: "filtersApplied",
      pagination: "pagination",
      totalCount: "totalCount",
      currentPage: "currentPage",
      displayCount: "displayCount",
    }),

    /**
     * The URL to POST to. Keep filter requests off querystring URLs.
     * When not mocking, use the current path without any search params.
     */
    postUrl() {
      if (!this.useMockData) {
        return window.location.pathname;
      }

      if (this.apiUrl) {
        try {
          return new URL(this.apiUrl, window.location.origin).pathname;
        } catch (e) {
          return this.apiUrl.split("?")[0];
        }
      }

      return window.location.pathname;
    },

    sizeBadgeText() {
      if (
        (this.minSize === "" || this.minSize === "0") &&
        (this.maxSize === "" || this.maxSize === "0")
      ) {
        return null;
      }
      return this.minSize + " - " + this.maxSize + " m²";
    },

    workplacesBadgeText() {
      if (
        (this.minNumberOfWorkplaces === "" ||
          this.minNumberOfWorkplaces === "0") &&
        (this.maxNumberOfWorkplaces === "" ||
          this.maxNumberOfWorkplaces === "0")
      ) {
        return null;
      }
      return (
        this.minNumberOfWorkplaces + " - " + this.maxNumberOfWorkplaces + " st"
      );
    },

    /**
     * Whether there are more pages to load
     */
    hasMorePages() {
      if (!this.pagination) return false;
      const pageCount =
        this.pagination.pageCount || this.pagination.PageCount || 1;
      const current = this.currentPage || 1;
      return current < pageCount;
    },
  },

  watch: {
    selectedFilters: {
      handler(newVal) {
        this.processedFilters = newVal.map((filter) => {
          let cleanedFilter = filter.split("-")[0].trim();
          return cleanedFilter
            .split(" ")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" ");
        });
      },
      deep: true,
    },
  },

  created() {
    // Handle translationString as either Object or JSON string
    if (typeof this.translationString === "string") {
      try {
        this.translate = JSON.parse(this.translationString);
      } catch (e) {
        this.translate = {};
      }
    } else {
      this.translate = this.translationString || {};
    }

    this.sortOptions.push(
      {
        display: this.translate.adressasc,
        value: 1,
      },
      {
        display: this.translate.adressdesc,
        value: 2,
      },
      {
        display: this.translate.latestobject,
        value: 3,
      },
      {
        display: this.translate.spacemin,
        value: 4,
      },
      {
        display: this.translate.spacemax,
        value: 5,
      },
    );

    this.activeSorting = this.sortOptions[2];
    this.activeSortingId = this.sortOptions[2].value;

    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);

    // only restore saved filters on browser back/forward
    const nav = performance.getEntriesByType("navigation")[0];
    const isBackForward = !!nav && nav.type === "back_forward";

    let restoredFromSession = false;

    if (isBackForward) {
      restoredFromSession = this.restoreFiltersFromSession();
    }

    if (restoredFromSession) {
      // keep the sort dropdown in sync with the restored sort id
      const restoredSort = this.sortOptions.find(
        (o) => o.value === this.activeSortingId,
      );
      if (restoredSort) this.activeSorting = restoredSort;
    } else {
      // fresh visit  (start page link, menu, refresh) → start clean - remove session storage and populate cityid/districtid from url/prop if exisit
      try {
        sessionStorage.removeItem(this.filterStorageKey());
      } catch (e) {}

      // props from backend Model, with URL as fallback
      const q = new URLSearchParams(window.location.search);
      const cityId = this.cityId || q.get("cityId");
      const districtId = this.districtId || q.get("districtId");

      if (cityId && cityId !== "0") this.selectedCityIds = [cityId];
      if (districtId && districtId !== "0")
        this.selectedDistrictIds = [districtId];

      this.saveFiltersToSession();
    }

    // Initial fetch via POST (or mock)
    const initialPayload = this.buildPayload(1);

    Promise.all([
      this.dispatchFetch(initialPayload, false),
      this.dispatchMapFetch(initialPayload),
    ]).then(() => {
      this.filteredObjects = this.currentObjects;
      this.isLoading = false;
      this.objectsFetched = true;

      // Initialise filter pills from the initial response: city/district names only
      const cityIds = this.selectedCityIds.map(String);
      const districtIds = this.selectedDistrictIds.map(String);
      const location = this.multiSelectFilters.find(
        (f) => f.name === "location",
      );

      if (location) {
        location.items.forEach((item) => {
          const ids = item.groupLabel ? cityIds : districtIds;
          if (
            ids.includes(String(item.id)) &&
            !this.selectedFilters.includes(item.value)
          ) {
            this.selectedFilters.push(item.value);
          }
        });
      }
      // Update sorting from server response
      this.updateSortingFromServer();
    });
  },

  destroyed() {
    window.removeEventListener("resize", this.checkScreenSize);
    if (this.filterDebounceTimer) {
      clearTimeout(this.filterDebounceTimer);
    }
  },

  mounted() {},
};
</script>
